import { forwardRef, useId, useState } from 'react';
import { EyeIcon, EyeOffIcon } from '../icons';
import { WordsHappenTextField, type WordsHappenTextFieldProps } from '../text-field';
import styles from './password-field.module.scss';

export interface WordsHappenPasswordFieldProps extends Omit<WordsHappenTextFieldProps, 'type' | 'rightSlot'> {
  /** Start with the password shown. */
  defaultVisible?: boolean;
}

/**
 * A WordsHappenTextField for passwords, with a button that shows or hides what was typed. The button is
 * named for what it will do next ("Show password" / "Hide password").
 */
export const WordsHappenPasswordField = forwardRef<HTMLInputElement, WordsHappenPasswordFieldProps>(
  ({ defaultVisible = false, id, disabled, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id ?? `${generatedId}-password`;
    const [visible, setVisible] = useState(defaultVisible);

    const toggle = (
      <button
        type="button"
        className={styles.toggle}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-controls={inputId}
        disabled={disabled}
        onClick={() => setVisible((shown) => !shown)}
      >
        {visible ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
      </button>
    );

    return (
      <WordsHappenTextField
        ref={ref}
        id={inputId}
        type={visible ? 'text' : 'password'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        disabled={disabled}
        rightSlot={toggle}
        {...rest}
      />
    );
  },
);

WordsHappenPasswordField.displayName = 'WordsHappenPasswordField';
